import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Calendar,
  MapPin,
  CreditCard,
  Clock,
  Users,
  ArrowRight,
  Plane,
  DollarSign,
  CheckCircle2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, token]);

  const fetchData = async () => {
    try {
      const [bookingsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/payments/transactions`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBookings(bookingsRes.data);
      setTransactions(transactionsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMakePayment = async (booking) => {
    try {
      const res = await axios.post(
        `${API}/payments/checkout`,
        {
          booking_id: booking.id,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      toast.error('Failed to initiate payment');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending_payment: { label: 'Pending Payment', className: 'bg-amber-100 text-amber-800' },
      active: { label: 'Active', className: 'bg-blue-100 text-blue-800' },
      completed: { label: 'Completed', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' }
    };
    const variant = variants[status] || variants.pending_payment;
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  const activeBookings = bookings.filter(b => b.status !== 'completed' && b.status !== 'cancelled');
  const completedBookings = bookings.filter(b => b.status === 'completed');
  const totalPaid = bookings.reduce((sum, b) => sum + b.amount_paid, 0);
  const totalRemaining = bookings.reduce((sum, b) => sum + b.remaining_amount, 0);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-muted/30" data-testid="user-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">
            Welcome back, {user?.full_name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground">Manage your bookings and payments</p>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                  <Plane className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Bookings</p>
                  <p className="text-2xl font-bold text-primary">{activeBookings.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold text-primary">{completedBookings.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Paid</p>
                  <p className="text-2xl font-bold text-primary">${totalPaid.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Remaining</p>
                  <p className="text-2xl font-bold text-primary">${totalRemaining.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList>
            <TabsTrigger value="bookings" data-testid="tab-bookings">My Bookings</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payment History</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            {bookings.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Plane className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-primary mb-2">No Bookings Yet</h3>
                  <p className="text-muted-foreground mb-4">Start your adventure by booking your first trip!</p>
                  <Button onClick={() => navigate('/trips')} className="bg-secondary hover:bg-secondary/90">
                    Explore Trips
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <Card key={booking.id} data-testid={`booking-card-${booking.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-heading text-lg font-semibold text-primary">
                              {booking.trip_title}
                            </h3>
                            {getStatusBadge(booking.status)}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              <span>{new Date(booking.tour_date).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              <span>{booking.travelers} Travelers</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <CreditCard className="h-4 w-4" />
                              <span>
                                {booking.payment_plan === 'full' ? 'Full Payment' : `${booking.payment_plan.replace('_', ' ')} plan`}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="lg:text-right">
                          <div className="flex items-center gap-4 mb-2">
                            <div>
                              <p className="text-sm text-muted-foreground">Paid</p>
                              <p className="font-semibold text-green-600">${booking.amount_paid.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Remaining</p>
                              <p className="font-semibold text-primary">${booking.remaining_amount.toLocaleString()}</p>
                            </div>
                          </div>
                          
                          {booking.total_payments > 1 && (
                            <div className="mb-3">
                              <div className="flex justify-between text-xs mb-1">
                                <span>Payment {booking.payments_made} of {booking.total_payments}</span>
                              </div>
                              <Progress 
                                value={(booking.payments_made / booking.total_payments) * 100} 
                                className="h-2"
                              />
                            </div>
                          )}

                          {booking.remaining_amount > 0 && booking.status !== 'cancelled' && (
                            <Button
                              onClick={() => handleMakePayment(booking)}
                              className="bg-secondary hover:bg-secondary/90"
                              data-testid={`make-payment-btn-${booking.id}`}
                            >
                              Make Payment (${booking.monthly_payment.toLocaleString()})
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="payments">
            {transactions.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-primary mb-2">No Payments Yet</h3>
                  <p className="text-muted-foreground">Your payment history will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                          <th className="text-left p-4 font-medium text-muted-foreground">Amount</th>
                          <th className="text-left p-4 font-medium text-muted-foreground">Payment #</th>
                          <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx) => (
                          <tr key={tx.id} className="border-t">
                            <td className="p-4">{new Date(tx.created_at).toLocaleDateString()}</td>
                            <td className="p-4 font-semibold">${tx.amount.toLocaleString()}</td>
                            <td className="p-4">{tx.payment_number} of {tx.total_payments}</td>
                            <td className="p-4">
                              <Badge className={tx.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                                {tx.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
